import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { FiShoppingBag, FiHeart, FiInfo } from "react-icons/fi";
import Logo from "../Assest/images/logo.png";
import Loader from './Loader';
import getSymbolFromCurrency from 'currency-symbol-map';
import axios from "axios";
import { useCurrency } from "./CurrencyContext";

const Perfume = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const { rate, currency } = useCurrency();

  const convertPrice = (price) => {
    const productPrice = parseFloat(price) || 0;
    const convertedPrice = productPrice * rate;
    return convertedPrice
  }
  const currencySymbol = currency === "SAR" ? "﷼" : getSymbolFromCurrency(currency) || currency;


  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSlideChange = (direction) => {
    if (isAnimating || !data || data.length === 0) return;
    setIsAnimating(true);
    setShowDetails(false);

    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        direction === "next"
          ? (prevIndex + 1) % data.length
          : (prevIndex - 1 + data.length) % data.length
      );
      setIsAnimating(false);
    }, 300);
  };

  const handleShopNow = useCallback(async (brandId, brandName) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/products/brand/${brandId}`);
      if (response.status === 200) {
        localStorage.setItem('apiProducts', JSON.stringify(response.data));

        navigate(`/product?brand=${brandName}&id=${brandId}`, {
          state: {
            apiData: response.data,
            brandId: brandId
          }
        });
      }
    } catch (error) {
      console.error("Error fetching company ", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  if (!data || data.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-800">No products available</p>
      </div>
    );
  }

  const currentPerfume = data[currentIndex];
  const imageUrl = currentPerfume.image 
    ? `${process.env.REACT_APP_API_URI}/uploads/${currentPerfume.image}`
    : '';

  // Default values for fields that might not be in the API data
  const perfumeDetails = {
    title: currentPerfume.product_name || "Premium Fragrance",
    subtitle: currentPerfume.brand_name || "Luxury Collection",
    price: currentPerfume.price ? convertPrice(currentPerfume.price).toFixed(2) : "$299.99",
    currency_code: currencySymbol,
    description: currentPerfume.description || "An exquisite fragrance crafted with premium ingredients for a luxurious experience.",
    color: "#F5E6E0", // Default color if not provided
    volume: "75ml", // Default volume
    concentration: "Eau de Parfum", // Default concentration
    notes: ["Floral", "Woody", "Spicy"], // Default notes
    badge: "PREMIUM" // Default badge
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Main Header */}
      {isLoading && <Loader Logo={Logo} />}
      <header className="text-center py-4 md:py-8 border-b border-gray-100">
        <h1 className="text-xl md:text-2xl font-light tracking-[0.2em] text-gray-800">LUXURY PROUDCTS</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-12">
        <div className="relative min-h-[500px] md:min-h-[600px] lg:min-h-[700px] bg-white rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.05)]">
          {/* Background Accent with subtle gradient */}
          <motion.div
            className="absolute inset-0 rounded-2xl opacity-30"
            animate={{
              background: `linear-gradient(135deg, ${perfumeDetails.color} 0%, white 100%)`
            }}
            transition={{ duration: 0.5 }}
          />

          {/* Gold accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200"></div>

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 p-4 md:p-8 lg:p-12">
            {/* Left Content - Image */}
            <div className="flex items-center justify-center lg:justify-start">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <img
                    src={imageUrl}
                    alt={perfumeDetails.title}
                    className="h-[300px] sm:h-[400px] md:h-[500px] object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      // e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                    }}
                  />
                  {/* Premium Badge with gold gradient */}
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-amber-700 via-yellow-600 to-amber-700 text-white text-xs px-4 py-2 rounded-full tracking-wider shadow-lg">
                    {perfumeDetails.badge}
                  </div>

                  {/* Reflection effect */}
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white/30 to-transparent transform scale-y-50 origin-bottom blur-sm"></div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right Content - Text */}
            <div className="flex flex-col justify-center space-y-4 md:space-y-6 lg:space-y-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-3 md:space-y-6"
                >
                  <span className="text-xs md:text-sm tracking-[0.3em] text-gray-500">
                    {perfumeDetails.subtitle}
                  </span>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900">
                    {perfumeDetails.title}
                  </h2>

                  {/* Price with gold accent */}
                  <div className="text-2xl md:text-3xl font-light bg-gradient-to-r from-amber-700 via-yellow-600 to-amber-700 text-transparent bg-clip-text">
                  {perfumeDetails?.currency_code} {perfumeDetails?.price}
                  </div>

                  <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-md">
                  {perfumeDetails.description.split(' ').slice(0, 20).join(' ')}
                  {perfumeDetails.description.split(' ').length > 20 && '...'}
                  </p>

                  {/* Additional premium details */}
                  <motion.div
                    className="space-y-3 pt-2"
                    animate={{ height: showDetails ? "auto" : "0px", opacity: showDetails ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {showDetails && (
                      <>
                        <div className="flex items-center space-x-4 text-sm md:text-base">
                          <div className="py-1 px-3 bg-gray-100 rounded">
                            <span className="font-medium">{perfumeDetails.volume}</span>
                          </div>
                          <div className="py-1 px-3 bg-gray-100 rounded">
                            <span className="font-medium">{perfumeDetails.concentration}</span>
                          </div>
                        </div>

                        <div className="pt-2">
                          <p className="text-xs md:text-sm text-gray-500 mb-2">KEY NOTES</p>
                          <div className="flex flex-wrap gap-2">
                            {perfumeDetails.notes.map((note, index) => (
                              <span
                                key={index}
                                className="inline-block py-1 px-3 bg-gray-100 rounded-full text-xs md:text-sm"
                              >
                                {note}
                              </span>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </motion.div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-6">
                    <button 
                      onClick={() => handleShopNow(currentPerfume.brand_id, currentPerfume.brand_name)} 
                      className="px-6 py-3 bg-black text-white tracking-wider hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 rounded-sm"
                    >
                      <FiShoppingBag size={18} />
                      <span>SHOP NOW</span>
                    </button>
                    <button
                      className="px-6 py-3 border border-black text-black tracking-wider hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-2 rounded-sm"
                      onClick={() => setShowDetails(!showDetails)}
                    >
                      <FiInfo size={18} />
                      <span>{showDetails ? "HIDE DETAILS" : "VIEW DETAILS"}</span>
                    </button>
                    <button className="sm:ml-auto p-3 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-100 transition-colors">
                      <FiHeart size={20} />
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation - Responsive positioning */}
          <div className="relative mt-12 w-full bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-4 sm:gap-8 sm:justify-centter  lg:bottom-12 sm:justify-start lg:justify-center pb-4 sm:pb-8 ml-0 lg:ml-0">
            {/* Previous Button */}
            <button
              onClick={() => handleSlideChange("prev")}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              aria-label="Previous perfume"
              disabled={!data || data.length === 0}
            >
              <IoChevronBack size={windowWidth < 640 ? 16 : 20} />
            </button>

            {/* Indicators */}
            <div className="flex gap-2 sm:gap-3">
              {data.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setShowDetails(false);
                    setCurrentIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${currentIndex === index
                    ? "w-6 sm:w-8 bg-gradient-to-r from-amber-700 via-yellow-600 to-amber-700"
                    : "w-2 bg-gray-300"
                    }`}
                  aria-label={`Go to perfume ${index + 1}`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => handleSlideChange("next")}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-black hover:text-white transition-colors"
              aria-label="Next perfume"
              disabled={!data || data.length === 0}
            >
              <IoChevronForward size={windowWidth < 640 ? 16 : 20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Perfume;